<script setup lang="ts">
import { storeToRefs } from 'pinia'
import Box from '@uiKit/elements/Box.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Text from '@uiKit/elements/Text.vue'
import { useToastStore } from '@uiKit/store/toastStore'
import type { PiIconName } from '@uiKit/elements/PiIcon.vue'
import type { ToastType } from '@shared/types/toast'

// Renders the toast queue. Mounted once at the app root so every layout is
// covered — the store and its callers already existed, but nothing had ever
// drawn them, so every addToast() in the codebase was silently discarded.
const toastStore = useToastStore()
const { toasts } = storeToRefs(toastStore)

const ICON: Record<ToastType, PiIconName> = {
  success: 'check-circle',
  error: 'times-circle',
  warning: 'exclamation-triangle',
  info: 'info-circle',
}

const ICON_COLOR: Record<ToastType, string> = {
  success: 'var(--p-success-600)',
  error: 'var(--p-danger-600)',
  warning: 'var(--p-warning-600)',
  info: 'var(--p-info-500)',
}
</script>

<template>
  <Teleport to="body">
    <Box direction="col" gap="2" class="toast-host" role="region" aria-label="Notifications">
      <TransitionGroup name="toast">
        <Box
          v-for="toast in toasts"
          :key="toast.id"
          align="start"
          gap="3"
          class="toast"
          role="status"
          aria-live="polite"
        >
          <PiIcon
            :name="ICON[toast.type]"
            class="toast__icon"
            :style="{ color: ICON_COLOR[toast.type] }"
          />
          <Text type="body" is="p" class="toast__message">{{ toast.message }}</Text>
          <Box
            is="button"
            flex
            align="center"
            justify="center"
            class="toast__close"
            type="button"
            aria-label="Dismiss notification"
            @click="toastStore.removeToast(toast.id)"
          >
            <PiIcon name="times" class="toast__close-icon" />
          </Box>
        </Box>
      </TransitionGroup>
    </Box>
  </Teleport>
</template>

<style scoped>
.toast-host {
  position: fixed;
  top: 1.25rem;
  right: 1.25rem;
  z-index: 2000;
  width: min(470px, calc(100vw - 2rem));
  pointer-events: none;
}

.toast {
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 12px;
  padding: 1rem 1.125rem;
  box-shadow: 0 12px 28px -8px rgba(10, 13, 18, 0.18);
  pointer-events: auto;
  box-sizing: border-box;
}

.toast__icon {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
}

.toast__message {
  flex: 1;
  min-width: 0;
}

.toast__close {
  width: 22px;
  height: 22px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: var(--p-text-muted-color);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}

.toast__close:hover {
  background: var(--p-surface-100);
}

.toast__close:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.toast__close-icon {
  font-size: 11px;
}

.toast-enter-active,
.toast-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.toast-move {
  transition: transform 0.2s ease;
}

@media (max-width: 992px) {
  .toast-host {
    top: 0.75rem;
    right: 0.75rem;
    left: 0.75rem;
    width: auto;
  }
}
</style>
